import React from 'react';
import { Form, Select } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { EPButton } from '@/components';
import { PlusOutlined } from '@ant-design/icons';
import { RoomContainer } from './RoomContainer.styles';
import TankSection from '../TankSection/TankSection';
import CompressorRow from '../CompressorRow/CompressorRow';
import { COMPRESSOR_DATA, ROOMS } from '@/store/SiteSurveyForm/constants';
import DryerSection from '../DryerSection/DryerSection';
import {
  getRoomName,
  RoomContainerProps,
  hasCompressorsOfType,
  getFilteredCompressorsWithIndex,
} from './RoomContainer.utils';

const RoomContainerComponent: React.FC<RoomContainerProps> = observer(
  ({
    room,
    pressureType,
    showDrawer,
    isMobile,
    isTablet,
    onRemoveRoom,
    onAddManualCompressor,
    onRemoveCompressor,
    onUpdateManualCompressorField,
    onOpenCompressorImageUpload,
    onEditCompressorImageTag,
    onRemoveCompressorImage,
    onAddTank,
    onRemoveTank,
    onAddDryer,
    onRemoveDryer,
    getAvailableCompressorsForRoom,
    getRoomIndex,
    renderCompressorNumberInput,
    renderModelSpecSelect,
    handleCompressorSelection,
    openCompressorDrawer,
    form,
    rooms,
  }) => {
    const roomIndex = getRoomIndex(rooms, room.id);

    const compressorField = [pressureType, ROOMS, roomIndex, COMPRESSOR_DATA];
    const compressorsModelSpec = useWatch(compressorField, form);

    const renderSelectComponent = (): React.ReactNode => {
      let additionalAttributes = {};
      if (showDrawer) {
        additionalAttributes = {
          onClick: () => openCompressorDrawer(room.id),
          open: false,
          tagRender: props => {
            const { label, closable, onClose } = props;
            return (
              <div className="compressor-tag">
                {label}
                {closable && (
                  <span className="compressor-tag-close" onClick={onClose}>
                    ×
                  </span>
                )}
              </div>
            );
          },
          onDeselect: value => {
            const newSelectedCompressors = room.compressorsInRoom.filter(sn => sn !== value);
            handleCompressorSelection(room.id, newSelectedCompressors);
          },
        };
      } else {
        additionalAttributes = {
          onChange: values => handleCompressorSelection(room.id, values as string[]),
        };
      }
      return (
        <Select
          mode="multiple"
          className="compressor-select"
          placeholder={t('Select available compressors')}
          size="large"
          options={getAvailableCompressorsForRoom(room.id)}
          optionFilterProp="label"
          value={room.compressorsInRoom}
          maxTagCount={2}
          maxTagPlaceholder={omittedValues => `+ ${omittedValues.length} more`}
          {...additionalAttributes}
        />
      );
    };

    return (
      <RoomContainer key={room.id}>
        <div className="room-header">
          <div className="room-title">{getRoomName(room, roomIndex)}</div>
          {rooms.length > 1 && (
            <div className="remove-room-link" onClick={() => onRemoveRoom(room.id)}>
              {t('Remove')}
            </div>
          )}
        </div>
        <div className="room-content">
          <div className="compressor-section">
            <div className="section-row">
              <div className="section-label">{t('Compressors in this room')}</div>
              <div className="control-group-class">
                <Form.Item
                  name={[pressureType, ROOMS, roomIndex, 'compressorsInRoom']}
                  className="compressor-select"
                  rules={[
                    {
                      required: !compressorsModelSpec?.length,
                      message: t('Each room must have at least one compressor'),
                    },
                  ]}
                >
                  {renderSelectComponent()}
                </Form.Item>
                <EPButton
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() => onAddManualCompressor(room.id)}
                  className="add-manual-compressor-button"
                  title={t('Add manual compressor')}
                >
                  {isMobile && <span className="button-text">{t('Add manually')}</span>}
                </EPButton>
              </div>
            </div>
            {hasCompressorsOfType(room.compressorData, false) && (
              <div className="compressors-table">
                {getFilteredCompressorsWithIndex(room.compressorData, false).map(({ compressor, originalIndex }) => (
                  <CompressorRow
                    key={`${roomIndex}-${originalIndex}`}
                    compressor={compressor}
                    roomId={room.id}
                    roomIndex={roomIndex}
                    index={originalIndex}
                    pressureType={pressureType}
                    isMobile={isMobile}
                    isTablet={isTablet}
                    onRemoveCompressor={onRemoveCompressor}
                    onUpdateManualCompressorField={onUpdateManualCompressorField}
                    onOpenCompressorImageUpload={onOpenCompressorImageUpload}
                    onEditCompressorImageTag={onEditCompressorImageTag}
                    onRemoveCompressorImage={onRemoveCompressorImage}
                    renderCompressorNumberInput={renderCompressorNumberInput}
                    renderModelSpecSelect={renderModelSpecSelect}
                    compressorsModelSpec={compressorsModelSpec}
                  />
                ))}
              </div>
            )}
          </div>

          {hasCompressorsOfType(room.compressorData, true) && (
            <div className="compressor-section">
              <div className="section-row">
                <div className="section-label">{t('Additional compressors')}</div>
              </div>
              <div className="compressors-table">
                {getFilteredCompressorsWithIndex(room.compressorData, true).map(({ compressor, originalIndex }) => (
                  <CompressorRow
                    key={`${roomIndex}-${originalIndex}`}
                    compressor={compressor}
                    roomId={room.id}
                    roomIndex={roomIndex}
                    index={originalIndex}
                    pressureType={pressureType}
                    isMobile={isMobile}
                    isTablet={isTablet}
                    onRemoveCompressor={onRemoveCompressor}
                    onUpdateManualCompressorField={onUpdateManualCompressorField}
                    onOpenCompressorImageUpload={onOpenCompressorImageUpload}
                    onEditCompressorImageTag={onEditCompressorImageTag}
                    onRemoveCompressorImage={onRemoveCompressorImage}
                    renderCompressorNumberInput={renderCompressorNumberInput}
                    renderModelSpecSelect={renderModelSpecSelect}
                    compressorsModelSpec={compressorsModelSpec}
                  />
                ))}
              </div>
            </div>
          )}

          <TankSection
            roomId={room.id}
            roomIndex={roomIndex}
            pressureType={pressureType}
            tanks={room.tanks}
            onAddTank={onAddTank}
            onRemoveTank={onRemoveTank}
          />
          <DryerSection
            roomId={room.id}
            roomIndex={roomIndex}
            pressureType={pressureType}
            dryers={room.dryers}
            onAddDryer={onAddDryer}
            onRemoveDryer={onRemoveDryer}
          />
        </div>
      </RoomContainer>
    );
  },
);

export default RoomContainerComponent;
